import { apiBase } from 'functions/server/apiBase';
import { getAuthToken } from 'functions/server/authToken';
import { parseBackendError, friendlyBackendMessage } from 'functions/server/backendError';
import { recordRequestFailure } from 'functions/server/requestFailure';


async function generic_backend_request_post(body, route) {
    /**
     * This function sends a POST request to the backend server at the specified route.
     *
     * When a user is signed in, the current Clerk session JWT is attached as an
     * `Authorization: Bearer` header so the backend can verify identity from the
     * token (soft-mode: it falls back to the body userId if the token is absent
     * or unverified — see functions/server/authToken.js).
     */
    const headers = {
        'Content-Type': 'application/json'
    };
    const token = await getAuthToken();
    if (token) {
        headers.Authorization = `Bearer ${token}`;
    }
    const requestOptions = {
        method: 'POST',
        headers,
        body: JSON.stringify(body)
    };
    if (route.startsWith('/')) {
        route = route.replace('/', '');
    }
    /* A rejected fetch is a failure with no HTTP status at all: DNS, a reset
       connection, TLS, a tab closing mid-flight. It used to be invisible to the
       telemetry, which only records inside the !response.ok branch -- so an
       incident where requests never completed would read as quieter than one
       where the server answered with errors. Recorded as status 0, the
       conventional "no response", so the two can never be conflated in a query.
       Rethrown unchanged: the caller's behaviour does not change. */
    let response;
    try {
        response = await fetch(apiBase() + '/' + route, requestOptions);
    } catch (networkError) {
        recordRequestFailure(route, 0, 'NETWORK_ERROR', 'POST');
        throw networkError;
    }
    if (!response.ok) {
        let body = null;
        try {
            body = await response.json();
        } catch (e) {
            // Not JSON (a gateway HTML page, an empty body). parseBackendError
            // falls back to the status line.
        }
        const { code, message, details } = parseBackendError(body, response.status, response.statusText);

        // The message IS the reason, so it can be shown directly. The old
        // version buried it behind "Backend request to X failed: 429 ..." and
        // then stringified our error object into "[object Object]", which told
        // the user nothing at all.
        const error = new Error(friendlyBackendMessage({ code, message, status: response.status, details }));
        error.code = code;                 // e.g. CONCURRENCY_LIMIT
        error.status = response.status;
        error.backendMessage = message;    // verbatim, for logs
        error.details = details;           // e.g. { kind: 'feature', feature: 'road_network_costs' }
        error.route = route;
        recordRequestFailure(route, response.status, code, 'POST');
        throw error;
    }
    const retJson = await response.json();
    return retJson.data;
}

export default generic_backend_request_post
